/**
 * A multi-service booking is considered available if all single-service bookings are available as returned from List Multi Service Availability Time Slots.
 * Currently, `SEPARATE_BOOKINGS` and `PARALLEL_BOOKINGS` are not supported.
 * Multi-service booking is available if each of its bookings is available separately.
 * For `SEQUENTIAL_BOOKINGS`, see `List Multi Service Availability Time Slots` documentation.
 */
export var MultiServiceBookingType;
(function (MultiServiceBookingType) {
    /** You must schedule single-service bookings back-to-back with each booking starting when the previous booking ends */
    MultiServiceBookingType["SEQUENTIAL_BOOKINGS"] = "SEQUENTIAL_BOOKINGS";
    /** Not currently supported. */
    MultiServiceBookingType["SEPARATE_BOOKINGS"] = "SEPARATE_BOOKINGS";
    /** Not currently supported. */
    MultiServiceBookingType["PARALLEL_BOOKINGS"] = "PARALLEL_BOOKINGS";
})(MultiServiceBookingType || (MultiServiceBookingType = {}));
export var LocationType;
(function (LocationType) {
    LocationType["UNDEFINED"] = "UNDEFINED";
    LocationType["OWNER_BUSINESS"] = "OWNER_BUSINESS";
    LocationType["OWNER_CUSTOM"] = "OWNER_CUSTOM";
    LocationType["CUSTOM"] = "CUSTOM";
})(LocationType || (LocationType = {}));
export var SelectionMethod;
(function (SelectionMethod) {
    SelectionMethod["UNKNOWN_SELECTION_METHOD"] = "UNKNOWN_SELECTION_METHOD";
    /** client picked resource id */
    SelectionMethod["SPECIFIC_RESOURCE"] = "SPECIFIC_RESOURCE";
    /** client explicitly asked for "any" */
    SelectionMethod["ANY_RESOURCE"] = "ANY_RESOURCE";
    /** client provided nothing */
    SelectionMethod["NO_SELECTION"] = "NO_SELECTION";
})(SelectionMethod || (SelectionMethod = {}));
export var ValueType;
(function (ValueType) {
    /** Short text. This is the default value type. */
    ValueType["SHORT_TEXT"] = "SHORT_TEXT";
    /** Long text. */
    ValueType["LONG_TEXT"] = "LONG_TEXT";
    /** A text that represents the check box value. If selected the value is `true`, otherwise `false`. */
    ValueType["CHECK_BOX"] = "CHECK_BOX";
})(ValueType || (ValueType = {}));
/** Booking status. */
export var BookingBookingStatus;
(function (BookingBookingStatus) {
    /** The booking was created, but the customer hasn't completed the related eCommerce order yet. */
    BookingBookingStatus["CREATED"] = "CREATED";
    /**
     * The merchant has confirmed the booking and it appears in the business calendar. Merchants can set up their services
     * ([SDK](https://dev.wix.com/docs/sdk/backend-modules/bookings/services/setup) | [REST](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/introduction)) to automatically confirm all `PENDING` bookings.
     */
    BookingBookingStatus["CONFIRMED"] = "CONFIRMED";
    /**
     * The customer has canceled the booking. Depending on the relevant service's policy snapshot
     * ([SDK](https://dev.wix.com/docs/sdk/backend-modules/bookings/booking-policy-snapshots/introduction) | [REST](https://dev.wix.com/docs/rest/business-solutions/bookings/policies/booking-policy-snapshots/introduction))
     * they may have to pay a cancellation fee ([SDK](https://dev.wix.com/docs/sdk/backend-modules/bookings/booking-fees/introduction) | [REST](https://dev.wix.com/docs/rest/business-solutions/bookings/pricing/booking-fees/introduction)).
     */
    BookingBookingStatus["CANCELED"] = "CANCELED";
    /** The merchant must manually confirm the booking before it appears in the business calendar. */
    BookingBookingStatus["PENDING"] = "PENDING";
    /** The merchant has declined the booking before the customer was charged. */
    BookingBookingStatus["DECLINED"] = "DECLINED";
    /**
     * The booking is on a waitlist.
     * Currently, you can't call Register to Waitlist ([REST](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings/waitlist/register-to-waitlist)) for course or appointment bookings, even though this is supported in live sites.
     * You can call Register to Waitlist only for class session bookings.
     */
    BookingBookingStatus["WAITING_LIST"] = "WAITING_LIST";
})(BookingBookingStatus || (BookingBookingStatus = {}));
/**
 * Payment status.
 * Automatically updated when using eCommerce checkout APIs.
 */
export var BookingPaymentStatus;
(function (BookingPaymentStatus) {
    /** Undefined payment status. */
    BookingPaymentStatus["UNDEFINED"] = "UNDEFINED";
    /** The booking isn't paid. */
    BookingPaymentStatus["NOT_PAID"] = "NOT_PAID";
    /** The booking is fully paid. */
    BookingPaymentStatus["PAID"] = "PAID";
    /** The booking is partially paid. */
    BookingPaymentStatus["PARTIALLY_PAID"] = "PARTIALLY_PAID";
    /** The booking is refunded. */
    BookingPaymentStatus["REFUNDED"] = "REFUNDED";
    /** The booking is free of charge. */
    BookingPaymentStatus["EXEMPT"] = "EXEMPT";
})(BookingPaymentStatus || (BookingPaymentStatus = {}));
/**
 * Selected payment option.
 *
 * One of the payment options offered by the service.
 * This field is set when the user selects an option during booking.
 * If left undefined, the payment option is resolved by the service configuration on checkout.
 */
export var V2SelectedPaymentOption;
(function (V2SelectedPaymentOption) {
    /** Undefined payment option. */
    V2SelectedPaymentOption["UNDEFINED"] = "UNDEFINED";
    /** Offline payment. */
    V2SelectedPaymentOption["OFFLINE"] = "OFFLINE";
    /** Online payment. */
    V2SelectedPaymentOption["ONLINE"] = "ONLINE";
    /** Payment using a Wix Pricing Plan. */
    V2SelectedPaymentOption["MEMBERSHIP"] = "MEMBERSHIP";
    /**
     * Customers can pay only in person with a Wix Pricing Plan, while the Wix user
     * must manually redeem the pricing plan in the dashboard.
     */
    V2SelectedPaymentOption["MEMBERSHIP_OFFLINE"] = "MEMBERSHIP_OFFLINE";
})(V2SelectedPaymentOption || (V2SelectedPaymentOption = {}));
export var BookingSourcePlatform;
(function (BookingSourcePlatform) {
    BookingSourcePlatform["UNDEFINED_PLATFORM"] = "UNDEFINED_PLATFORM";
    BookingSourcePlatform["WEB"] = "WEB";
    BookingSourcePlatform["MOBILE_APP"] = "MOBILE_APP";
})(BookingSourcePlatform || (BookingSourcePlatform = {}));
export var BookingSourceActor;
(function (BookingSourceActor) {
    BookingSourceActor["UNDEFINED_ACTOR"] = "UNDEFINED_ACTOR";
    BookingSourceActor["BUSINESS"] = "BUSINESS";
    BookingSourceActor["CUSTOMER"] = "CUSTOMER";
})(BookingSourceActor || (BookingSourceActor = {}));
export var IdentityType;
(function (IdentityType) {
    IdentityType["UNKNOWN"] = "UNKNOWN";
    IdentityType["ANONYMOUS_VISITOR"] = "ANONYMOUS_VISITOR";
    IdentityType["MEMBER"] = "MEMBER";
    IdentityType["WIX_USER"] = "WIX_USER";
    IdentityType["APP"] = "APP";
})(IdentityType || (IdentityType = {}));
export var LocationLocationType;
(function (LocationLocationType) {
    /** Undefined location type. */
    LocationLocationType["UNDEFINED"] = "UNDEFINED";
    /** The business address as set in the site’s general settings. */
    LocationLocationType["OWNER_BUSINESS"] = "OWNER_BUSINESS";
    /** The address set when creating the service. */
    LocationLocationType["OWNER_CUSTOM"] = "OWNER_CUSTOM";
    /** The address set for the individual session. */
    LocationLocationType["CUSTOM"] = "CUSTOM";
})(LocationLocationType || (LocationLocationType = {}));
export var WebhookIdentityType;
(function (WebhookIdentityType) {
    WebhookIdentityType["UNKNOWN"] = "UNKNOWN";
    WebhookIdentityType["ANONYMOUS_VISITOR"] = "ANONYMOUS_VISITOR";
    WebhookIdentityType["MEMBER"] = "MEMBER";
    WebhookIdentityType["WIX_USER"] = "WIX_USER";
    WebhookIdentityType["APP"] = "APP";
})(WebhookIdentityType || (WebhookIdentityType = {}));
export var IdentificationDataIdentityType;
(function (IdentificationDataIdentityType) {
    IdentificationDataIdentityType["UNKNOWN"] = "UNKNOWN";
    IdentificationDataIdentityType["ANONYMOUS_VISITOR"] = "ANONYMOUS_VISITOR";
    IdentificationDataIdentityType["MEMBER"] = "MEMBER";
    IdentificationDataIdentityType["WIX_USER"] = "WIX_USER";
    IdentificationDataIdentityType["APP"] = "APP";
})(IdentificationDataIdentityType || (IdentificationDataIdentityType = {}));
export var SortOrder;
(function (SortOrder) {
    SortOrder["ASC"] = "ASC";
    SortOrder["DESC"] = "DESC";
})(SortOrder || (SortOrder = {}));
//# sourceMappingURL=types.impl.js.map